import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DMView = () => {
  const { currentDm, dms, selectDm, createDM, friends } = useApp();
  const { user, token } = useAuth();
  const { on } = useSocket();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    if (!currentDm || !token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/dms/${currentDm.dm_id}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setMessages(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentDm, token]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  useEffect(() => {
    if (!on) return;
    const unsub = on('DM_MESSAGE', (event) => {
      if (event.data.dm_id === currentDm?.dm_id) {
        setMessages(prev => [...prev, event.data]);
      }
    });
    return () => unsub?.();
  }, [on, currentDm]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentDm) return;
    const content = newMessage;
    setNewMessage('');
    try {
      await fetch(`${API_URL}/api/dms/${currentDm.dm_id}/messages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content })
      });
    } catch (e) { console.error(e); }
  };

  const otherUser = currentDm?.user;

  if (!currentDm) {
    return (
      <div className="flex-1 flex flex-col min-h-0 min-w-0 bg-[#0b0d10]" data-testid="dm-view-empty">
        {/* DM List */}
        <div className="h-12 px-4 flex items-center border-b border-white/5">
          <span className="text-sm font-semibold text-white">{t('app.directMessages')}</span>
        </div>
        <ScrollArea className="flex-1 p-2">
          {dms.length === 0 ? (
            <p className="text-center text-zinc-500 text-sm py-8">No conversations yet</p>
          ) : (
            dms.map(dm => (
              <button
                key={dm.dm_id}
                onClick={() => selectDm(dm)}
                data-testid={`dm-item-${dm.dm_id}`}
                className="flex items-center gap-3 w-full px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <Avatar className="h-9 w-9">
                  <AvatarImage src={dm.user?.avatar} />
                  <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-xs">
                    {(dm.user?.display_name || dm.user?.username || '?')[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-sm font-medium text-white truncate">{dm.user?.display_name || dm.user?.username}</p>
                  {dm.last_message && (
                    <p className="text-xs text-zinc-500 truncate">{dm.last_message.content}</p>
                  )}
                </div>
                {dm.unread_count > 0 && (
                  <span className="h-5 min-w-5 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {dm.unread_count}
                  </span>
                )}
              </button>
            ))
          )}
        </ScrollArea>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 min-w-0 bg-[#0b0d10]" data-testid="dm-view">
      {/* Header */}
      <div className="h-12 px-4 flex items-center gap-3 border-b border-white/5 flex-shrink-0">
        <Avatar className="h-6 w-6">
          <AvatarImage src={otherUser?.avatar} />
          <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-[10px]">
            {(otherUser?.display_name || otherUser?.username || '?')[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <span className="font-semibold text-sm text-white">{otherUser?.display_name || otherUser?.username}</span>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-2">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="h-6 w-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          messages.map(msg => {
            const isMine = msg.user_id === user?.user_id;
            const msgUser = msg.user || {};
            return (
              <div key={msg.message_id} className="flex gap-3 px-2 py-1.5 hover:bg-white/[0.02] rounded" data-testid={`dm-msg-${msg.message_id}`}>
                <Avatar className="h-8 w-8 flex-shrink-0">
                  <AvatarImage src={msgUser.avatar} />
                  <AvatarFallback className="bg-cyan-600/20 text-cyan-300 text-xs">
                    {(msgUser.display_name || msgUser.username || '?')[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-white">{msgUser.display_name || msgUser.username}</span>
                    <span className="text-[10px] text-zinc-600">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <p className="text-sm text-zinc-200">{msg.content}</p>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </ScrollArea>

      {/* Input */}
      <form onSubmit={sendMessage} className="p-3 flex-shrink-0" data-testid="dm-message-form">
        <div className="flex items-center gap-2 bg-[#12141a] rounded-lg px-3 border border-white/5 focus-within:border-cyan-500/30 transition-colors">
          <input
            type="text"
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            placeholder={`Message ${otherUser?.display_name || otherUser?.username}`}
            className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none"
            data-testid="dm-message-input"
          />
          <button type="submit" disabled={!newMessage.trim()} className="text-zinc-500 hover:text-cyan-400 disabled:opacity-30 transition-colors p-1" data-testid="dm-send-btn">
            <Send className="h-4 w-4" />
          </button>
        </div>
      </form>
    </div>
  );
};
